import Fuse from 'fuse.js';
import blockGeolocation from '../stores/blockGeolocation';
import handleFetch from './handleFetch';

interface SearchParams {
  limit?: number;
}

interface ForwardGeocodingParams extends SearchParams {
  value: string;
}

interface ReverseGeocodingParams extends SearchParams {
  lng: number;
  lat: number;
}

type MapBoxFeatureContext = {
  id: string;
  text: string;
  short_code?: string;
};

type MapBoxFeature = {
  id: string;
  address?: string;
  text: string;
  place_type: [string];
  center: [number, number];
  context: MapBoxFeatureContext[];
};

type MapBoxResponseData = {
  query: string[];
  features: MapBoxFeature[];
};

export interface Location extends MapBoxFeature {
  detected: boolean;
  street: string;
  zip: string;
  city: string;
  state: string;
  html: string;
}

type GeolocationCoordinates = {
  longitude: number;
  latitude: number;
};

type GeolocationPosition = {
  coords: GeolocationCoordinates;
};

type GeolocationPositionError = {
  code: number;
  message: string;
};

export const MAPBOX_NAVIGATION_SERVICE = `${process.env.MAPBOX_API_ENDPOINT}/geocoding/v5/mapbox.places`;

const defaultParams = {
  access_token: process.env.MAPBOX_API_KEY || '',
  country: 'us',
};

const paramsString = (obj: { [key: string]: string }): string => {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([key, value]) => {
    params.append(key, value);
  });
  return params.toString();
};

const handleResponse = (res: Response): Promise<MapBoxResponseData> =>
  new Promise((resolve, reject) => {
    if (res?.status === 200) {
      res.json().then(resolve);
    } else {
      reject(res);
    }
  });

const handleError = (defaultValue: Array<Location>) => (): Array<Location> => defaultValue;

export const transformMapBoxFeature =
  (detected = false) =>
  (feature: MapBoxFeature): Location => {
    const location = feature as Location;
    const type = feature.place_type[0];

    location.detected = detected;

    if (type === 'address') {
      location.street = feature.address ? feature.address + ' ' + feature.text : '';
    } else {
      location.street = '';
    }

    if (type === 'postcode') {
      location.zip = feature.text;
    } else {
      location.zip = feature.context.find((context) => context.id.match(/^postcode/))?.text || '';
    }

    if (type === 'place') {
      location.city = feature.text;
    } else {
      location.city = feature.context.find((context) => context.id.match(/^place/))?.text || '';
    }

    location.state =
      feature.context.find((context) => context.id.match(/^region/))?.short_code?.replace('US-', '') ||
      feature.context.find((context) => context.id.match(/^country/))?.short_code?.toUpperCase() ||
      '';

    location.html = location.street || location.zip || location.city || '';

    return location;
  };

type MatchFragmentIndice = [number, number];
type MatchFragment = {
  matches: boolean;
  indices: MatchFragmentIndice;
};

const allMatchFragments = (matchedFragments: readonly MatchFragmentIndice[], lastIndex: number): MatchFragment[] => {
  return matchedFragments.reduce((acc: MatchFragment[], [start, end], i) => {
    const previous = matchedFragments[i - 1] || [];

    const [, previousEnd] = previous;

    if (previousEnd >= 0) {
      acc.push({ matches: false, indices: [previousEnd + 1, start - 1] });
    } else if (start !== 0) {
      acc.push({ matches: false, indices: [0, start - 1] });
    }

    acc.push({ matches: true, indices: [start, end] });

    if (i === matchedFragments.length - 1 && end !== lastIndex) {
      acc.push({ matches: false, indices: [end + 1, lastIndex] });
    }

    return acc;
  }, []);
};

const highlight =
  (str: string, tag = 'strong') =>
  ({ matches, indices }: MatchFragment): string => {
    const [start, end] = indices;
    const stringFragment = str.slice(start, end + 1);
    return matches ? `<${tag}>${stringFragment}</${tag}>` : stringFragment;
  };

const transformMapBoxData =
  ({ detected = false, value = '' } = {}) =>
  (data: MapBoxResponseData): Location[] => {
    const features = data.features.map(transformMapBoxFeature(detected));

    if (value) {
      return new Fuse(features, {
        shouldSort: true,
        includeMatches: true,
        threshold: 1.0,
        keys: ['html'],
      })
        .search(value.trim())
        .map(({ item, matches }) => {
          if (matches) {
            const { indices, value } = matches[0];
            if (indices && value) {
              item.html = allMatchFragments(indices, value.length - 1)
                .map(highlight(value))
                .join('');
            }
          }
          return item;
        });
    }

    return features;
  };

let forwardGeocodingAbortController: AbortController | null = null;

export const forwardGeocoding = ({ value, limit = 8 }: ForwardGeocodingParams): Promise<Location[]> => {
  forwardGeocodingAbortController?.abort();
  forwardGeocodingAbortController = new AbortController();

  const qs = paramsString({
    ...defaultParams,
    autocomplete: 'true',
    types: 'address,postcode,place',
    limit: String(limit),
  });

  return handleFetch(`${MAPBOX_NAVIGATION_SERVICE}/${value}.json?${qs}`, {
    signal: forwardGeocodingAbortController.signal,
    errorKey: 'Problem retrieving location from Mapbox.',
  })
    .then(handleResponse)
    .then(transformMapBoxData({ value }))
    .catch(handleError([]));
};

let reverseGeocodingAbortController: AbortController | null = null;

const reverseGeocoding = ({ lng, lat, limit = 8 }: ReverseGeocodingParams): Promise<Location[]> => {
  reverseGeocodingAbortController?.abort();
  reverseGeocodingAbortController = new AbortController();

  const qs = paramsString({
    ...defaultParams,
    types: 'address',
    limit: String(limit),
  });

  return handleFetch(`${MAPBOX_NAVIGATION_SERVICE}/${lng},${lat}.json?${qs}`, {
    signal: reverseGeocodingAbortController.signal,
    errorKey: 'Error in reverse geocoding with Mapbox.',
  })
    .then(handleResponse)
    .then(transformMapBoxData({ detected: true }))
    .catch(handleError([]));
};

export const getZip = (location: Location): Promise<string> =>
  reverseGeocoding({ lng: location.center[0], lat: location.center[1] }).then((loc) => loc[0]?.zip || '');
export const currentLocation = (): Promise<Location | null> => {
  const geolocation: Promise<GeolocationPosition> = new Promise((resolve, reject) => {
    const errorCallback = (error: GeolocationPositionError): void => {
      //  user block geolocation permission
      if (error.code === 1) {
        blockGeolocation.set(true);
      }
      reject(error);
    };
    navigator.geolocation.getCurrentPosition(resolve, errorCallback, {
      enableHighAccuracy: true,
      timeout: Number(process.env.GEOLOCATION_API_TIMEOUT) || 5000,
      maximumAge: 0,
    });
  });
  return geolocation
    .then(({ coords }: GeolocationPosition) => {
      const { longitude: lng, latitude: lat } = coords;
      return reverseGeocoding({ lng, lat, limit: 1 });
    })
    .then((locations: Location[]) => locations[0] || null)
    .catch(() => null);
};
